/**
 * PhotoUploadGrid — shared handoff photo upload
 *
 * Renders one or more photo upload sections ("categories"). Each section
 * shows a tile grid of thumbnails + an "Add photo" tile that launches the
 * device camera on mobile. Per-photo progress, per-photo retry on failure,
 * and a live "X of N photos added" readout.
 *
 * External contract:
 *   - photos (controlled): { [categoryKey]: [{ key, viewUrl, uploadedAt }, ...] }
 *     Only COMPLETED uploads appear in this prop — in-flight/errored
 *     entries live in the component's local state.
 *   - onPhotosChange(categoryKey, updatedCompletedPhotos): fires whenever
 *     the completed-photo array for a category changes (add, remove).
 *
 * Usage:
 *   <PhotoUploadGrid
 *     categories={[{ key: 'condition', label: 'Condition photos', required: true }]}
 *     photos={photos}
 *     onPhotosChange={handleChange}
 *     transactionId={txId}
 *     minPhotosPerCategory={4}
 *     maxPhotosPerCategory={10}
 *   />
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { uploadToS3 } from '../../util/s3Upload';
import { IconClose } from '../index';

import css from './PhotoUploadGrid.module.css';

// Local-only shape (not sent to parent):
//   { id, file, previewUrl, status: 'uploading'|'error'|'done', progress, errorMessage }
const localId = () => `up_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const PhotoUploadGrid = props => {
  const {
    categories = [],
    photos = {},
    onPhotosChange,
    transactionId,
    minPhotosPerCategory = 0,
    maxPhotosPerCategory = 10,
    disabled = false,
    className,
  } = props;

  const intl = useIntl();

  // In-flight and errored uploads — keyed by category. Completed uploads
  // live in the `photos` prop (parent-owned). We never duplicate a
  // completed photo here.
  const [localEntries, setLocalEntries] = useState({});

  // Map from localId → AbortController so we can cancel on remove.
  const abortControllers = useRef({});

  const fileInputRefs = useRef({});

  const classes = classNames(css.root, className);

  // Track latest localEntries in a ref so the unmount cleanup below
  // revokes the current set of blob URLs rather than the stale empty
  // map captured at mount.
  const localEntriesRef = useRef(localEntries);
  useEffect(() => {
    localEntriesRef.current = localEntries;
  });

  // Track latest `photos` prop in a ref too. This is load-bearing for
  // multi-file uploads: without it, each startUpload's .then() closure
  // reads the stale `photos` prop value captured at the time the handler
  // was created. With 4 concurrent uploads, all 4 resolve having read
  // photos=[] and call onPhotosChange([theirOwnFile]), so only the last
  // write wins and the first 3 completed uploads disappear.
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  });

  // Revoke object URLs on unmount to avoid leaking blob memory. Safe to
  // leave the dep array empty: we only read via the ref, which is not
  // a reactive value.
  useEffect(() => {
    return () => {
      Object.values(localEntriesRef.current || {}).forEach(entries =>
        (entries || []).forEach(e => {
          if (e.previewUrl) URL.revokeObjectURL(e.previewUrl);
        })
      );
    };
  }, []);

  const setEntryForCategory = (categoryKey, updater) => {
    setLocalEntries(prev => {
      const current = prev[categoryKey] || [];
      return { ...prev, [categoryKey]: updater(current) };
    });
  };

  const patchEntry = (categoryKey, id, patch) => {
    setEntryForCategory(categoryKey, entries =>
      entries.map(e => (e.id === id ? { ...e, ...patch } : e))
    );
  };

  const removeLocalEntry = (categoryKey, id) => {
    setEntryForCategory(categoryKey, entries => {
      const removed = entries.find(e => e.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return entries.filter(e => e.id !== id);
    });
  };

  const startUpload = useCallback(
    (categoryKey, file) => {
      const id = localId();
      const previewUrl = URL.createObjectURL(file);
      const controller = new AbortController();
      abortControllers.current[id] = controller;

      setEntryForCategory(categoryKey, entries => [
        ...entries,
        { id, file, previewUrl, status: 'uploading', progress: 0, errorMessage: null },
      ]);

      const onProgress = pct => patchEntry(categoryKey, id, { progress: pct });

      uploadToS3({
        file,
        transactionId,
        category: categoryKey,
        onProgress,
        signal: controller.signal,
      })
        .then(result => {
          // Promote to completed — append via the ref to read the very
          // latest parent-owned photos array, not the stale closure copy.
          const completed = {
            key: result.key,
            viewUrl: result.viewUrl,
            uploadedAt: result.uploadedAt,
          };
          const latestPhotos = photosRef.current || {};
          const nextCompleted = [...(latestPhotos[categoryKey] || []), completed];
          onPhotosChange(categoryKey, nextCompleted);
          removeLocalEntry(categoryKey, id);
          delete abortControllers.current[id];
        })
        .catch(err => {
          delete abortControllers.current[id];
          // Cancelled uploads are silent — the entry has already been
          // removed by the cancel path.
          if (err && err.message && err.message.includes('Upload cancelled')) return;
          patchEntry(categoryKey, id, {
            status: 'error',
            errorMessage: err?.message || 'Upload failed. Tap to retry.',
          });
        });

      return id;
    },
    // photosRef is stable; startUpload only needs to re-create when
    // the transaction or callback identity changes.
    [onPhotosChange, transactionId]
  );

  const retryEntry = (categoryKey, id) => {
    setEntryForCategory(categoryKey, entries => {
      const target = entries.find(e => e.id === id);
      if (!target) return entries;
      const controller = new AbortController();
      abortControllers.current[id] = controller;
      // Kick a fresh upload for the same File, reset status.
      const onProgress = pct => patchEntry(categoryKey, id, { progress: pct });
      uploadToS3({
        file: target.file,
        transactionId,
        category: categoryKey,
        onProgress,
        signal: controller.signal,
      })
        .then(result => {
          const completed = {
            key: result.key,
            viewUrl: result.viewUrl,
            uploadedAt: result.uploadedAt,
          };
          const latestPhotos = photosRef.current || {};
          const nextCompleted = [...(latestPhotos[categoryKey] || []), completed];
          onPhotosChange(categoryKey, nextCompleted);
          removeLocalEntry(categoryKey, id);
          delete abortControllers.current[id];
        })
        .catch(err => {
          delete abortControllers.current[id];
          if (err && err.message && err.message.includes('Upload cancelled')) return;
          patchEntry(categoryKey, id, {
            status: 'error',
            errorMessage: err?.message || 'Upload failed. Tap to retry.',
          });
        });
      return entries.map(e =>
        e.id === id ? { ...e, status: 'uploading', progress: 0, errorMessage: null } : e
      );
    });
  };

  const handleFileSelect = (categoryKey, files) => {
    if (disabled) return;
    const currentCompleted = photos[categoryKey] || [];
    const currentLocal = localEntries[categoryKey] || [];
    const taken = currentCompleted.length + currentLocal.length;
    const remaining = Math.max(0, maxPhotosPerCategory - taken);
    if (remaining <= 0) return;
    const filesToProcess = Array.from(files).slice(0, remaining);
    filesToProcess.forEach(file => startUpload(categoryKey, file));
  };

  const handleInputChange = categoryKey => event => {
    const files = event.target.files;
    if (files && files.length > 0) handleFileSelect(categoryKey, files);
    event.target.value = '';
  };

  const triggerFileInput = categoryKey => {
    if (disabled) return;
    const input = fileInputRefs.current[categoryKey];
    if (input) input.click();
  };

  const handleRemoveCompleted = (categoryKey, index) => {
    if (disabled) return;
    const current = photos[categoryKey] || [];
    const next = current.filter((_, i) => i !== index);
    onPhotosChange(categoryKey, next);
  };

  const handleRemoveLocal = (categoryKey, id) => {
    if (disabled) return;
    const controller = abortControllers.current[id];
    if (controller) controller.abort();
    delete abortControllers.current[id];
    removeLocalEntry(categoryKey, id);
  };

  return (
    <div className={classes}>
      {categories.map(category => {
        const categoryCompleted = photos[category.key] || [];
        const categoryLocal = localEntries[category.key] || [];
        const completedCount = categoryCompleted.length;
        const totalCount = completedCount + categoryLocal.length;
        const canAddMore = totalCount < maxPhotosPerCategory;
        const minMet = completedCount >= minPhotosPerCategory;

        return (
          <div key={category.key} className={css.categorySection}>
            {category.label ? (
              <div className={css.categoryHeader}>
                <h3 className={css.categoryTitle}>
                  {category.label}
                  {category.required ? (
                    <span className={css.requiredIndicator}>
                      {intl.formatMessage({ id: 'PhotoUploadGrid.required' })}
                    </span>
                  ) : null}
                </h3>
                {category.subtext ? (
                  <p className={css.categorySubtext}>{category.subtext}</p>
                ) : null}
              </div>
            ) : null}

            <div className={css.photoGrid}>
              {categoryCompleted.map((photo, index) => (
                <div key={photo.key || `done-${index}`} className={css.photoSlot}>
                  <img
                    src={photo.viewUrl}
                    alt={intl.formatMessage(
                      { id: 'PhotoUploadGrid.photoAlt' },
                      { label: category.label || 'photo', n: index + 1 }
                    )}
                    className={css.photoThumbnail}
                  />
                  {!disabled ? (
                    <button
                      type="button"
                      className={css.removeButton}
                      onClick={() => handleRemoveCompleted(category.key, index)}
                      aria-label={intl.formatMessage({ id: 'PhotoUploadGrid.removePhoto' })}
                    >
                      <IconClose />
                    </button>
                  ) : null}
                </div>
              ))}

              {categoryLocal.map(entry => (
                <div
                  key={entry.id}
                  className={classNames(css.photoSlot, {
                    [css.photoSlotUploading]: entry.status === 'uploading',
                    [css.photoSlotError]: entry.status === 'error',
                  })}
                >
                  <img
                    src={entry.previewUrl}
                    alt={intl.formatMessage({ id: 'PhotoUploadGrid.uploadingAlt' })}
                    className={css.photoThumbnail}
                  />
                  {entry.status === 'uploading' ? (
                    <>
                      <div
                        className={css.progressOverlay}
                        aria-live="polite"
                        aria-label={intl.formatMessage(
                          { id: 'PhotoUploadGrid.uploadingPercent' },
                          { percent: entry.progress }
                        )}
                      >
                        <div className={css.progressBarWrap}>
                          <div
                            className={css.progressBar}
                            style={{ width: `${entry.progress}%` }}
                          />
                        </div>
                        <span className={css.progressText}>{entry.progress}%</span>
                      </div>
                      <button
                        type="button"
                        className={css.removeButton}
                        onClick={() => handleRemoveLocal(category.key, entry.id)}
                        aria-label={intl.formatMessage({ id: 'PhotoUploadGrid.cancelUpload' })}
                      >
                        <IconClose />
                      </button>
                    </>
                  ) : null}
                  {entry.status === 'error' ? (
                    <>
                      <button
                        type="button"
                        className={css.retryButton}
                        onClick={() => retryEntry(category.key, entry.id)}
                        aria-label={intl.formatMessage({ id: 'PhotoUploadGrid.retryUpload' })}
                      >
                        <span className={css.retryLabel}>
                          {intl.formatMessage({ id: 'PhotoUploadGrid.retry' })}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={css.removeButton}
                        onClick={() => handleRemoveLocal(category.key, entry.id)}
                        aria-label={intl.formatMessage({ id: 'PhotoUploadGrid.removePhoto' })}
                      >
                        <IconClose />
                      </button>
                    </>
                  ) : null}
                </div>
              ))}

              {canAddMore && !disabled ? (
                <button
                  type="button"
                  className={css.addPhotoButton}
                  onClick={() => triggerFileInput(category.key)}
                  aria-label={`${intl.formatMessage({
                    id: 'PhotoUploadGrid.addPhoto',
                  })} - ${category.label || ''}`}
                >
                  <span className={css.addIcon} aria-hidden="true" style={{ fontSize: 24 }}>📷</span>
                  <span className={css.addText}>
                    {intl.formatMessage({ id: 'PhotoUploadGrid.addPhoto' })}
                  </span>
                </button>
              ) : null}

              <input
                ref={el => {
                  if (el) fileInputRefs.current[category.key] = el;
                }}
                type="file"
                accept="image/jpeg,image/png"
                capture="environment"
                multiple={maxPhotosPerCategory > 1}
                onChange={handleInputChange(category.key)}
                className={css.hiddenFileInput}
              />
            </div>

            {/* Live "X of N" readout. Confirms goal met with a check,
                or tells the user how many more to add. */}
            <div
              className={classNames(css.countReadout, {
                [css.countReadoutMet]: minMet && minPhotosPerCategory > 0,
              })}
              aria-live="polite"
            >
              {minPhotosPerCategory > 0 && !minMet ? (
                <>
                  {intl.formatMessage(
                    { id: 'PhotoUploadGrid.addNMore' },
                    { n: minPhotosPerCategory - completedCount }
                  )}
                </>
              ) : minPhotosPerCategory > 0 && minMet ? (
                <>
                  {intl.formatMessage(
                    { id: 'PhotoUploadGrid.countMet' },
                    { count: completedCount, min: minPhotosPerCategory }
                  )}
                </>
              ) : (
                <>
                  {intl.formatMessage(
                    { id: 'PhotoUploadGrid.countBare' },
                    { count: completedCount, max: maxPhotosPerCategory }
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PhotoUploadGrid;
